import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, Target, Award, Share2 } from "lucide-react";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { CurveBottom, Cloud } from "../components/Decor";
import { getModuleBySlug, modules, STEPS } from "../data/mock";
import { useProgress } from "../context/ProgressContext";
import Quiz from "../components/module/Quiz";
import Worksheet from "../components/module/Worksheet";
import Cheers from "../components/Cheers";
import RecapCard from "../components/RecapCard";
import LessonDayShow from "../components/LessonDayShow";
import LessonIntro from "../components/LessonIntro";

const Icon = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

const StepNav = ({ m, active, setActive, done }) => (
  <div className="flex items-center justify-between gap-1 sm:gap-2 overflow-x-auto pb-2" data-testid="step-nav">
    {STEPS.map((s, i) => {
      const isActive = active === s.id;
      const isDone = done[s.id] !== undefined && done[s.id] !== false;
      return (
        <React.Fragment key={s.id}>
          <button
            onClick={() => setActive(s.id)}
            className={`flex flex-col items-center gap-1.5 min-w-[72px] group`}
            data-testid={`step-${s.id}`}
          >
            <span
              className={`w-14 h-14 rounded-full flex items-center justify-center border-4 transition-[transform,background-color,border-color] duration-200 group-hover:scale-105 ${isActive ? "scale-110 shadow-lg" : ""}`}
              style={{
                background: isDone ? "#2e8b57" : isActive ? m.color : "#ffffff",
                borderColor: isActive ? m.dark : isDone ? "#2e8b57" : "#e9f9fd",
                color: isDone ? "#fff" : isActive ? m.dark : "#1b8bc9",
              }}
            >
              {isDone ? <Check size={24} strokeWidth={3} /> : <Icon name={s.icon} size={24} />}
            </span>
            <span className={`text-xs font-bold ${isActive ? "text-[#0c4a6e]" : "text-gray-500"}`}>{s.label}</span>
          </button>
          {i < STEPS.length - 1 && <span className="hidden sm:block flex-1 h-1.5 rounded-full" style={{ background: isDone ? "#2e8b57" : "#e9f9fd", minWidth: 12 }} />}
        </React.Fragment>
      );
    })}
  </div>
);

const Learn = ({ m, onDone }) => (
  <div className="space-y-8">
    <div className="bg-[#e9f9fd] rounded-3xl p-6">
      <h3 className="font-bold text-[#0c4a6e] flex items-center gap-2 text-lg"><Target size={20} className="text-[#e5356f]" /> By the end, your child can...</h3>
      <ul className="mt-3 space-y-2">
        {m.objectives.map((o, i) => (
          <li key={i} className="flex gap-3 text-gray-700"><span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: m.button }}>{i + 1}</span>{o}</li>
        ))}
      </ul>
    </div>
    <div className="grid md:grid-cols-3 gap-6">
      {m.concepts.map((c, i) => (
        <div key={i} className="bg-white rounded-3xl p-6 shadow-md border-t-8 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300" style={{ borderColor: m.color }} data-testid={`concept-card-${i}`}>
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${m.color}44`, color: m.dark }}><Icon name={c.icon} size={24} /></span>
          <h4 className="font-bold text-[#0c4a6e] text-lg mt-4" style={{ fontFamily: "Literata, serif" }}>{c.title}</h4>
          <p className="text-gray-600 mt-2 leading-relaxed text-[15px]">{c.text}</p>
        </div>
      ))}
    </div>
    <div className="text-center">
      <button onClick={onDone} className="btn-pill-lg text-white shadow-lg" style={{ background: m.button }} data-testid="learn-done-button">Got it - let's talk about it</button>
    </div>
  </div>
);

const Talk = ({ m, panda, onDone }) => {
  const [shown, setShown] = useState(1);
  const all = shown >= m.script.length;
  return (
    <div className="max-w-2xl mx-auto">
      <p className="text-center text-gray-600 mb-6">A conversation you can have this week. Tap to reveal each line.</p>
      <div className="space-y-4" data-testid="script-bubbles">
        {m.script.slice(0, shown).map((line, i) => {
          const isParent = line.who === "parent";
          return (
            <div key={i} className={`flex items-end gap-3 bubble-in ${isParent ? "" : "flex-row-reverse"}`}>
              {isParent ? (
                <span className="w-12 h-12 rounded-full bg-[#7d5fb2] text-white flex items-center justify-center font-bold flex-shrink-0">P</span>
              ) : (
                <span className="w-12 h-12 rounded-full bg-[#e9f9fd] flex items-center justify-center flex-shrink-0 overflow-hidden"><Panda size={38} accessory={panda?.accessory || "none"} scarf={panda?.scarf || "#e5356f"} /></span>
              )}
              <div className={`rounded-3xl px-5 py-3 max-w-[80%] shadow ${isParent ? "bg-white rounded-bl-md" : "text-white rounded-br-md"}`} style={!isParent ? { background: m.button } : {}}>
                <p className={`text-xs font-bold mb-1 ${isParent ? "text-[#7d5fb2]" : "text-white/80"}`}>{isParent ? "Parent" : panda?.name || (m.num >= 5 ? "Young adult" : m.num === 4 ? "Teen" : "Child")}</p>
                <p className="leading-relaxed">{line.text}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 text-center">
        {!all ? (
          <button onClick={() => setShown((s) => s + 1)} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white shadow" data-testid="script-next-button">Next line</button>
        ) : (
          <button onClick={onDone} className="btn-pill-lg text-white shadow-lg" style={{ background: m.button }} data-testid="talk-done-button">We talked about it!</button>
        )}
      </div>
    </div>
  );
};

const Story = ({ m, onDone }) => (
  <div className="max-w-3xl mx-auto">
    <div className="relative bg-white rounded-3xl shadow-lg p-8 sm:p-10 overflow-hidden">
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full opacity-30" style={{ background: m.color }} />
      <p className="text-xs uppercase tracking-widest font-bold" style={{ color: m.dark }}>Case study</p>
      <h3 className="text-3xl font-bold text-[#0c4a6e] mt-1" style={{ fontFamily: "Literata, serif" }}>{m.caseStudy.title}</h3>
      <p className="mt-6 text-gray-700 text-lg leading-relaxed relative">{m.caseStudy.text}</p>
      <div className="mt-6 rounded-2xl p-5 flex gap-4 items-start" style={{ background: `${m.color}33` }}>
        <Panda size={54} accessory={m.pandaAccessory} scarf={m.pandaScarf} />
        <div>
          <p className="font-bold text-[#0c4a6e]">The lesson</p>
          <p className="text-gray-700 mt-1">{m.caseStudy.lesson}</p>
        </div>
      </div>
    </div>
    <div className="text-center mt-8">
      <button onClick={onDone} className="btn-pill-lg text-white shadow-lg" style={{ background: m.button }} data-testid="story-done-button">Story read!</button>
    </div>
  </div>
);

const DoDont = ({ m, onDone }) => (
  <div className="max-w-4xl mx-auto">
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-[#e8f8ef] rounded-3xl p-7 border-2 border-[#63c79a]">
        <h3 className="flex items-center gap-2 text-2xl font-bold text-[#0d4d33]" style={{ fontFamily: "Literata, serif" }}><ThumbsUp className="text-[#2e8b57]" /> Do</h3>
        <ul className="mt-4 space-y-3">{m.dos.map((d, i) => <li key={i} className="flex gap-3 text-gray-700"><Check className="text-[#2e8b57] flex-shrink-0 mt-0.5" size={20} />{d}</li>)}</ul>
      </div>
      <div className="bg-[#fff0f5] rounded-3xl p-7 border-2 border-[#ee7aa6]">
        <h3 className="flex items-center gap-2 text-2xl font-bold text-[#7a1f45]" style={{ fontFamily: "Literata, serif" }}><ThumbsDown className="text-[#e5356f]" /> Don't</h3>
        <ul className="mt-4 space-y-3">{m.donts.map((d, i) => <li key={i} className="flex gap-3 text-gray-700"><Icons.X className="text-[#e5356f] flex-shrink-0 mt-0.5" size={20} />{d}</li>)}</ul>
      </div>
    </div>
    <div className="text-center mt-8">
      <button onClick={onDone} className="btn-pill-lg text-white shadow-lg" style={{ background: m.button }} data-testid="dodont-done-button">Ready for the quiz!</button>
    </div>
  </div>
);

const ModuleWorld = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const m = getModuleBySlug(slug);
  const { completed, completeStep, panda, moduleProgress, activeChild } = useProgress();
  const active = params.get("step") || "learn";
  const setActive = (id) => setParams({ step: id });

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [active]);

  const [recapOpen, setRecapOpen] = useState(false);
  const [recapAuto, setRecapAuto] = useState(false);
  const pct = m ? moduleProgress(m.id) : 0;
  const prevPct = useRef(pct);
  useEffect(() => {
    if (prevPct.current < 100 && pct === 100 && activeChild) { setRecapAuto(true); setRecapOpen(true); }
    prevPct.current = pct;
  }, [pct, activeChild]);

  if (!m) return <div className="p-20 text-center">Lesson not found. <Link to="/lessons" className="text-[#1b8bc9] underline">Back to lessons</Link></div>;

  const done = completed[m.id] || {};
  const idx = STEPS.findIndex((s) => s.id === active);
  const next = STEPS[idx + 1];
  const prevMod = modules[m.num - 2];
  const nextMod = modules[m.num];

  const finish = (step) => {
    const first = done[step] === undefined;
    completeStep(m.id, step);
    if (first) toast.success("+5 bamboo earned!", { description: `${STEPS[idx].label} complete` });
    if (next) setActive(next.id);
  };

  const stepsDone = Object.keys(done).filter((k) => k !== "certifiedAt" && done[k] !== undefined && done[k] !== false).length;

  const render = () => {
    switch (active) {
      case "learn": return <Learn m={m} onDone={() => finish("learn")} />;
      case "talk": return <Talk m={m} panda={panda} onDone={() => finish("talk")} />;
      case "practice": return <Worksheet m={m} onDone={() => finish("practice")} />;
      case "story": return <Story m={m} onDone={() => finish("story")} />;
      case "dodont": return <DoDont m={m} onDone={() => finish("dodont")} />;
      case "quiz": return <Quiz m={m} />;
      default: return null;
    }
  };

  return (
    <div data-testid="module-page">
      <section className="relative overflow-hidden" style={{ background: m.color }}>
        <Cloud className="absolute right-[10%] top-6 hidden md:block cloud-drift opacity-90" size={160} />
        <div className="relative max-w-5xl mx-auto px-6 pt-8 pb-24">
          <Link to="/lessons" className="inline-flex items-center gap-1 font-bold text-sm hover:underline" style={{ color: m.dark }} data-testid="back-to-lessons"><ChevronLeft size={16} /> All lessons</Link>
          <div className="mt-4 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="flex-shrink-0 relative">
              <div className="absolute inset-x-6 bottom-2 h-5 bg-black/10 rounded-full blur-md" />
              <Panda size={170} accessory={panda?.accessory || m.pandaAccessory} scarf={panda?.scarf || m.pandaScarf} item={m.pandaItem} animate />
            </div>
            <div className="text-center md:text-left">
              <p className="text-sm font-bold uppercase tracking-widest" style={{ color: m.dark }}>Lesson {m.num} &middot; Ages {m.ages}</p>
              <h1 className="text-white font-bold text-3xl sm:text-5xl leading-tight drop-shadow-[0_2px_0_rgba(0,0,0,0.25)]" style={{ fontFamily: "Literata, serif" }}>{m.title}</h1>
              <p className="mt-3 text-lg font-medium" style={{ color: m.dark }}>{m.tagline}</p>
              <div className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-3">
                <div className="inline-flex items-center gap-3 bg-white/80 rounded-full px-4 py-2">
                  <div className="h-2.5 w-40 bg-[#e9f9fd] rounded-full overflow-hidden"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${moduleProgress(m.id)}%`, background: m.button }} /></div>
                  <span className="text-sm font-bold text-[#0c4a6e]" data-testid="module-progress">{moduleProgress(m.id)}%</span>
                </div>
                {stepsDone > 0 && activeChild && (
                  <button onClick={() => { setRecapAuto(false); setRecapOpen(true); }} className="inline-flex items-center gap-2 btn-pill bg-white/90 text-[#0c4a6e] shadow hover:bg-white" data-testid="recap-button">
                    <Share2 size={16} /> Recap
                  </button>
                )}
                {moduleProgress(m.id) === 100 && (
                  <Link to={`/certificate/${m.slug}`} className="inline-flex items-center gap-2 btn-pill bg-white text-[#8a6d00] shadow hover:bg-[#fff8e1] cert-pop" data-testid="certificate-cta">
                    <Award size={18} className="text-[#f3c932]" fill="#f3c932" /> Certificate earned - print it!
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <section className="bg-[#f7fdfe] pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {activeChild && <div className="-mt-6 relative z-10"><LessonIntro child={activeChild} moduleId={m.id} /></div>}
          <div className="bg-white rounded-3xl shadow-lg p-4 sm:p-6 -mt-6 relative z-10">
            <StepNav m={m} active={active} setActive={setActive} done={done} />
          </div>
          {!panda && (
            <div className="mt-6 bg-[#fff8e1] border-2 border-[#f3c932] rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-3 text-[#5a4600]" data-testid="no-child-banner">
              <p className="flex-1 text-sm">You can explore freely, but progress and bamboo are only saved once you <span className="font-bold">create a panda</span> for your child.</p>
              <Link to="/panda" className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white text-sm">Create a panda</Link>
            </div>
          )}
          {activeChild && <div className="mt-6"><Cheers child={activeChild} compact /></div>}
          <LessonDayShow child={activeChild} />
          <RecapCard open={recapOpen} onOpenChange={setRecapOpen} module={m} child={activeChild} autoOpened={recapAuto} />
          <div className="mt-10 step-in" key={active}>{render()}</div>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-[#e9f9fd] pt-8">
            {prevMod ? (
              <button onClick={() => navigate(`/lessons/${prevMod.slug}`)} className="inline-flex items-center gap-2 font-bold text-[#1b8bc9] hover:text-[#e5356f]"><ChevronLeft size={18} /> Lesson {prevMod.num}: {prevMod.short}</button>
            ) : <span />}
            {nextMod ? (
              <button onClick={() => navigate(`/lessons/${nextMod.slug}`)} className="inline-flex items-center gap-2 font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid="next-module-link">Lesson {nextMod.num}: {nextMod.short} <ChevronRight size={18} /></button>
            ) : <Link to="/parents/fundamentals" className="font-bold text-[#1b8bc9] hover:text-[#e5356f]">Explore the Parents' area <ChevronRight size={18} className="inline" /></Link>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModuleWorld;
